// Client/src/pages/digi/Invoice/Components/TemplatesTab.jsx
//
// A vendor's invoice templates: start one from a ready-made layout, open it in
// the designer (/invoice-templates/:id), set the invoice numbering, and send it
// to the company for approval. Once approved, the vendor's invoices are
// generated from it and attaching an invoice file becomes optional.

import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Paper, Tooltip } from "@mui/material";
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, Spinner } from "reactstrap";
import FeatherIcon from "feather-icons-react";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import {
  listTemplates,
  getStarters,
  createTemplate,
  updateTemplate,
  duplicateTemplate,
  makeTemplateDefault,
  archiveTemplate,
  submitTemplate,
  getTemplateCompanies,
  APPROVAL_LABEL,
} from "./templateApi";

const ACCENT = "#4F46E5";
const TONE = {
  warning: { bg: "#FFFBEB", fg: "#92400E", border: "#FDE68A", icon: "clock" },
  success: { bg: "#ECFDF5", fg: "#065F46", border: "#A7F3D0", icon: "check-circle" },
  danger: { bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA", icon: "alert-circle" },
  neutral: { bg: "#F1F5F9", fg: "#475569", border: "#CBD5E1", icon: "edit-3" },
};

const btn = (variant = "primary") => ({
  display: "inline-flex", alignItems: "center", gap: 6, minHeight: 34,
  borderRadius: 8, padding: "6px 12px", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
  ...(variant === "primary"
    ? { background: ACCENT, color: "#fff", border: "none", boxShadow: "0 2px 8px rgba(79,70,229,0.25)" }
    : variant === "danger"
      ? { background: "#FEF2F2", color: "#B91C1C", border: "1px solid #FECACA" }
      : { background: "#fff", color: "#334155", border: "1px solid #E2E8F0" }),
});

/** "GSK " + 271 → "GSK 271"; padded to `pad` digits. Mirrors the server's formatVendorNumber. */
export const formatVendorNumber = (n) =>
  n ? `${n.prefix || ""}${String(n.nextNo || 1).padStart(Number(n.pad) || 0, "0")}` : "";

const Pill = ({ tone, icon, children, title }) => {
  const t = TONE[tone] || TONE.neutral;
  return (
    <span title={title} style={{
      display: "inline-flex", alignItems: "center", gap: 5, borderRadius: 20, padding: "3px 10px",
      fontSize: "0.72rem", fontWeight: 700, background: t.bg, color: t.fg, border: `1px solid ${t.border}`,
    }}>
      <FeatherIcon icon={icon || t.icon} size={11} /> {children}
    </span>
  );
};

// A schematic of a starter's layout in its own colours: enough to tell the
// layouts apart without rendering them.
const StarterThumb = ({ design }) => {
  const pal = design?.page?.palette || {};
  const primary = pal.primary || "#1f3864";
  const accent = pal.accent || "#9dc3e6";
  const surface = pal.surface || "#ffffff";
  const bands = design?.bands || [];
  const headerFilled = !!bands[0]?.style?.background;
  return (
    <div aria-hidden="true" style={{
      width: "100%", aspectRatio: "8.5 / 11", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 6,
      padding: headerFilled ? 0 : 10, display: "flex", flexDirection: "column", gap: 6, overflow: "hidden",
    }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        background: headerFilled ? primary : "transparent", padding: headerFilled ? 10 : 0, minHeight: 34,
      }}>
        <div style={{ width: 34, height: 20, borderRadius: 3, background: headerFilled ? "rgba(255,255,255,0.35)" : accent }} />
        <div style={{ width: 42, height: 8, borderRadius: 2, background: headerFilled ? "#fff" : primary }} />
      </div>
      <div style={{ display: "flex", gap: 8, padding: headerFilled ? "0 10px" : 0 }}>
        <div style={{ flex: 1, height: 6, background: "#E2E8F0", borderRadius: 2 }} />
        <div style={{ flex: 1, height: 6, background: "#E2E8F0", borderRadius: 2 }} />
      </div>
      <div style={{ padding: headerFilled ? "0 10px" : 0, display: "flex", flexDirection: "column", gap: 2, marginTop: 4 }}>
        <div style={{ height: 8, background: accent === "#ffffff" ? primary : accent, borderRadius: 1 }} />
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ height: 6, background: i % 2 ? (surface !== "#ffffff" ? surface : "#F1F5F9") : "#fff", border: "1px solid #F1F5F9" }} />
        ))}
        <div style={{ alignSelf: "flex-end", width: "40%", height: 14, background: accent, marginTop: 3, borderRadius: 1 }} />
      </div>
    </div>
  );
};

// ── Starter gallery ──────────────────────────────────────────────────────

const StarterGallery = ({ isOpen, onClose, onCreated }) => {
  const [starters, setStarters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [starterId, setStarterId] = useState("classic");
  const [name, setName] = useState("My invoice");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName("My invoice");
    setStarterId("classic");
    setLoading(true);
    getStarters().then((s) => setStarters(Array.isArray(s) ? s : [])).catch(() => setStarters([])).finally(() => setLoading(false));
  }, [isOpen]);

  const create = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const t = await createTemplate({ name: name.trim(), starterId });
      onCreated(t);
    } catch {
      // toast shown by the api helper
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={() => !creating && onClose()} size="xl" centered scrollable>
      <ModalHeader toggle={() => !creating && onClose()}>Start a new invoice template</ModalHeader>
      <ModalBody>
        <div style={{ fontSize: "0.82rem", color: "#475569", marginBottom: 14 }}>
          Pick a layout to start from. Everything — logo, colours, text, columns — can be changed in the designer.
        </div>
        {loading ? (
          <div style={{ textAlign: "center", padding: 40 }}><Spinner style={{ color: ACCENT }} /></div>
        ) : (
          <div role="radiogroup" aria-label="Starting layout"
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))", gap: 14 }}>
            {starters.map((s) => {
              const on = s.id === starterId;
              return (
                <button key={s.id} type="button" role="radio" aria-checked={on} onClick={() => setStarterId(s.id)}
                  style={{
                    textAlign: "left", background: on ? "#EEF2FF" : "#fff", cursor: "pointer",
                    border: `2px solid ${on ? ACCENT : "#E2E8F0"}`, borderRadius: 12, padding: 10,
                    display: "flex", flexDirection: "column", gap: 8, outlineOffset: 2,
                  }}>
                  <StarterThumb design={s.design} />
                  <div>
                    <div style={{ fontWeight: 800, fontSize: "0.85rem", color: "#0F172A", display: "flex", alignItems: "center", gap: 6 }}>
                      {s.name}
                      {on && <FeatherIcon icon="check-circle" size={14} color={ACCENT} />}
                    </div>
                    <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: 2 }}>{s.description}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
        <div style={{ marginTop: 18, maxWidth: 360 }}>
          <Label for="tpl-name" style={{ fontWeight: 700, fontSize: "0.8rem" }}>Template name</Label>
          <Input id="tpl-name" value={name} maxLength={80} onChange={(e) => setName(e.target.value)}
            invalid={!name.trim()} />
          {!name.trim() && <div role="alert" style={{ color: "#DC2626", fontSize: "0.74rem", marginTop: 4 }}>Give the template a name</div>}
        </div>
      </ModalBody>
      <ModalFooter>
        <button type="button" style={btn("secondary")} onClick={onClose} disabled={creating}>Cancel</button>
        <button type="button" style={{ ...btn("primary"), opacity: creating || !name.trim() ? 0.6 : 1 }}
          disabled={creating || !name.trim() || loading} onClick={create}>
          {creating ? <Spinner size="sm" style={{ width: 14, height: 14 }} /> : <FeatherIcon icon="arrow-right" size={14} />}
          Open in designer
        </button>
      </ModalFooter>
    </Modal>
  );
};

// ── Numbering ────────────────────────────────────────────────────────────

const PREFIX_RX = /^[A-Za-z0-9 #/-]*$/;

const NumberingDialog = ({ template, onClose, onSaved }) => {
  const [prefix, setPrefix] = useState("");
  const [nextNo, setNextNo] = useState("1");
  const [pad, setPad] = useState("0");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!template) return;
    setPrefix(template.numbering?.prefix || "");
    setNextNo(String(template.numbering?.nextNo || 1));
    setPad(String(template.numbering?.pad || 0));
  }, [template]);

  const prefixError = prefix.length > 12 || !PREFIX_RX.test(prefix)
    ? "Up to 12 letters, digits, spaces, # / or -" : "";
  const n = Number(nextNo);
  const nextError = !Number.isInteger(n) || n < 1 || n > 9999999 ? "A whole number from 1 to 9,999,999" : "";
  const p = Number(pad);
  const padError = !Number.isInteger(p) || p < 0 || p > 8 ? "0 to 8 digits" : "";
  const invalid = !!(prefixError || nextError || padError);

  const save = async () => {
    if (invalid) return;
    setSaving(true);
    try {
      await updateTemplate(template._id, { numbering: { prefix, nextNo: n, pad: p } });
      toast.success("Numbering saved");
      onSaved();
    } catch {
      // toast shown by the api helper
    } finally {
      setSaving(false);
    }
  };

  const err = (m) => m ? <div role="alert" style={{ color: "#DC2626", fontSize: "0.72rem", marginTop: 3 }}>{m}</div> : null;

  return (
    <Modal isOpen={!!template} toggle={() => !saving && onClose()} centered>
      <ModalHeader toggle={() => !saving && onClose()}>Invoice numbering</ModalHeader>
      <ModalBody>
        <div style={{ fontSize: "0.8rem", color: "#475569", marginBottom: 14 }}>
          Your own invoice number, filled into “Vendor&apos;s Invoice #” on each new invoice. It counts up by one each time it is used.
        </div>
        <div className="row g-3">
          <div className="col-5">
            <Label for="num-prefix" style={{ fontWeight: 700, fontSize: "0.78rem" }}>Prefix</Label>
            <Input id="num-prefix" value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="e.g. GSK " invalid={!!prefixError} />
            {err(prefixError)}
          </div>
          <div className="col-4">
            <Label for="num-next" style={{ fontWeight: 700, fontSize: "0.78rem" }}>Next number</Label>
            <Input id="num-next" type="number" min={1} value={nextNo} onChange={(e) => setNextNo(e.target.value)} invalid={!!nextError} />
            {err(nextError)}
          </div>
          <div className="col-3">
            <Label for="num-pad" style={{ fontWeight: 700, fontSize: "0.78rem" }}>Digits</Label>
            <Input id="num-pad" type="number" min={0} max={8} value={pad} onChange={(e) => setPad(e.target.value)} invalid={!!padError} />
            {err(padError)}
          </div>
        </div>
        <div aria-live="polite" style={{
          marginTop: 16, background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 10, padding: "10px 14px",
          fontSize: "0.82rem", color: "#334155",
        }}>
          Next invoice: <strong style={{ color: "#0F172A" }}>{invalid ? "—" : formatVendorNumber({ prefix, nextNo: n, pad: p })}</strong>
          {!invalid && <span style={{ color: "#94A3B8" }}> · then {formatVendorNumber({ prefix, nextNo: n + 1, pad: p })}</span>}
        </div>
      </ModalBody>
      <ModalFooter>
        <button type="button" style={btn("secondary")} onClick={onClose} disabled={saving}>Cancel</button>
        <button type="button" style={{ ...btn("primary"), opacity: invalid || saving ? 0.6 : 1 }} disabled={invalid || saving} onClick={save}>
          {saving ? <Spinner size="sm" style={{ width: 14, height: 14 }} /> : <FeatherIcon icon="check" size={14} />} Save
        </button>
      </ModalFooter>
    </Modal>
  );
};

// ── Submit for approval ──────────────────────────────────────────────────

export const SubmitDialog = ({ template, onClose, onSubmitted }) => {
  const [companies, setCompanies] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!template) return;
    setLoading(true);
    getTemplateCompanies()
      .then((list) => {
        const arr = Array.isArray(list) ? list : [];
        setCompanies(arr);
        setSelected(new Set(arr.map((c) => String(c._id))));
      })
      .catch(() => setCompanies([]))
      .finally(() => setLoading(false));
  }, [template]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const submit = async () => {
    if (!selected.size) return;
    setSubmitting(true);
    try {
      await submitTemplate(template._id, Array.from(selected));
      toast.success("Sent for approval. You'll get an email when it's reviewed.");
      onSubmitted();
    } catch {
      // toast shown by the api helper (lists what needs fixing)
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={!!template} toggle={() => !submitting && onClose()} centered>
      <ModalHeader toggle={() => !submitting && onClose()}>Submit “{template?.name}” for approval</ModalHeader>
      <ModalBody>
        <div style={{ fontSize: "0.8rem", color: "#475569", marginBottom: 12 }}>
          The company checks your template once. After it is approved, your invoices for that company are
          generated from it — you no longer need to attach an invoice file. Until then, nothing changes.
        </div>
        {loading ? (
          <div style={{ textAlign: "center", padding: 20 }}><Spinner size="sm" style={{ color: ACCENT }} /></div>
        ) : companies.length === 0 ? (
          <div style={{ color: "#B91C1C", fontSize: "0.82rem" }}>
            You are not set up to invoice any company yet, so there is no one to send it to.
          </div>
        ) : (
          <fieldset>
            <legend style={{ fontSize: "0.78rem", fontWeight: 700, color: "#334155" }}>Send to</legend>
            {companies.map((c) => (
              <label key={c._id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.84rem", margin: "6px 0", cursor: "pointer" }}>
                <input type="checkbox" checked={selected.has(String(c._id))} onChange={() => toggle(String(c._id))} />
                {c.name}
              </label>
            ))}
          </fieldset>
        )}
      </ModalBody>
      <ModalFooter>
        <button type="button" style={btn("secondary")} onClick={onClose} disabled={submitting}>Cancel</button>
        <button type="button" style={{ ...btn("primary"), opacity: !selected.size || submitting ? 0.6 : 1 }}
          disabled={!selected.size || submitting} onClick={submit}>
          {submitting ? <Spinner size="sm" style={{ width: 14, height: 14 }} /> : <FeatherIcon icon="send" size={14} />}
          Submit for approval
        </button>
      </ModalFooter>
    </Modal>
  );
};

// ── Tab ──────────────────────────────────────────────────────────────────

const TemplatesTab = () => {
  const history = useHistory();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [numbering, setNumbering] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await listTemplates(showArchived ? { includeArchived: "1" } : {});
      setTemplates(Array.isArray(list) ? list : []);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const openDesigner = (id) => history.push(`/invoice-templates/${id}`);

  const run = async (t, fn, ok) => {
    setBusyId(t._id);
    try {
      await fn();
      if (ok) toast.success(ok);
      load();
    } catch {
      // toast shown by the api helper
    } finally {
      setBusyId(null);
    }
  };

  const archive = async (t) => {
    const res = await Swal.fire({
      title: `Archive “${t.name}”?`,
      text: "Invoices already generated keep their PDF. New invoices stop using this template.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Archive",
      confirmButtonColor: "#DC2626",
    });
    if (res.isConfirmed) run(t, () => archiveTemplate(t._id), "Template archived");
  };

  const approvedSomewhere = templates.some((t) => (t.approvals || []).some((a) => a.approvedVersion > 0));

  return (
    <>
      <Paper elevation={0} className="iv-wrap iv-paper">
        <div className="iv-header">
          <div className="iv-header__left">
            <div className="iv-header__icon-wrap">
              <FeatherIcon icon="layout" size={18} color="#A5B4FC" />
            </div>
            <div className="iv-header__meta">
              <div className="iv-header__label">Invoices</div>
              <div className="iv-header__title">Invoice Templates</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.78rem", color: "#475569", margin: 0, cursor: "pointer" }}>
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Show archived
            </label>
            <button type="button" style={btn("primary")} onClick={() => setGalleryOpen(true)}>
              <FeatherIcon icon="plus" size={14} /> New template
            </button>
          </div>
        </div>

        <div style={{ padding: 16 }}>
          {!loading && templates.length > 0 && !approvedSomewhere && (
            <div role="note" style={{
              display: "flex", gap: 10, alignItems: "flex-start", background: "#EEF2FF", border: "1px solid #C7D2FE",
              borderRadius: 10, padding: "10px 14px", fontSize: "0.8rem", color: "#3730A3", marginBottom: 14,
            }}>
              <FeatherIcon icon="info" size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Until a company approves one of your templates, keep attaching your invoice file to each invoice.
                When you are happy with the design, press <strong>Submit for approval</strong>.
              </span>
            </div>
          )}

          {loading && <div style={{ textAlign: "center", padding: "40px 0" }}><Spinner style={{ color: ACCENT }} /></div>}

          {!loading && !templates.length && (
            <div className="iv-empty" style={{ minHeight: "30vh" }}>
              <div className="iv-empty__icon-wrap"><FeatherIcon icon="layout" size={24} color={ACCENT} /></div>
              <div className="iv-empty__title">Design your invoice once</div>
              <div className="iv-empty__sub" style={{ maxWidth: 500 }}>
                Build your own invoice layout — logo, colours, wording, columns. After the company approves it,
                every invoice you submit is turned into your invoice automatically, so you don&apos;t need to attach a file.
              </div>
              <button type="button" style={{ ...btn("primary"), marginTop: 14 }} onClick={() => setGalleryOpen(true)}>
                <FeatherIcon icon="plus" size={14} /> Create a template
              </button>
            </div>
          )}

          {!loading && templates.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 }}>
              {templates.map((t) => {
                const busy = busyId === t._id;
                const approvals = t.approvals || [];
                return (
                  <div key={t._id} style={{
                    border: `1px solid ${t.isDefault ? "#C7D2FE" : "#E2E8F0"}`, borderRadius: 12, padding: 16,
                    background: t.isArchived ? "#F8FAFC" : "#fff", display: "flex", flexDirection: "column", gap: 10,
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "flex-start" }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, color: "#0F172A", fontSize: "0.92rem", overflowWrap: "anywhere" }}>{t.name}</div>
                        <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: 2 }}>
                          Edited {t.updatedAt ? moment(t.updatedAt).fromNow() : "—"}
                          {t.versionCount ? ` · ${t.versionCount} version${t.versionCount !== 1 ? "s" : ""} submitted` : ""}
                        </div>
                      </div>
                      <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                        {t.isDefault && <span className="iv-chip iv-chip--indigo"><FeatherIcon icon="star" size={10} /> Default</span>}
                        {t.isArchived && <span className="iv-chip iv-chip--gray">Archived</span>}
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {!t.versionCount && <div><Pill tone="neutral">Draft — not submitted yet</Pill></div>}
                      {approvals.map((a) => {
                        const label = APPROVAL_LABEL[a.status] || { text: a.status, tone: "neutral" };
                        return (
                          <div key={String(a.companyId)} style={{ fontSize: "0.76rem", color: "#334155" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <span style={{ fontWeight: 700 }}>{a.companyName || "Company"}</span>
                              <Pill tone={label.tone}>{label.text} · v{a.version}</Pill>
                              {a.status !== "approved" && a.approvedVersion > 0 && (
                                <span style={{ color: "#64748B" }}>v{a.approvedVersion} still in use</span>
                              )}
                            </div>
                            {a.status === "changes_requested" && a.note && (
                              <div style={{
                                marginTop: 4, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8,
                                padding: "6px 10px", color: "#7F1D1D", whiteSpace: "pre-wrap",
                              }}>
                                “{a.note}”
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {t.versionCount > 0 && t.hasUnsubmittedChanges && !t.isArchived && (
                        <div style={{ fontSize: "0.74rem", color: "#92400E" }}>
                          <FeatherIcon icon="edit-3" size={11} /> You have changes that are not submitted yet.
                        </div>
                      )}
                    </div>

                    <div style={{ fontSize: "0.76rem", color: "#475569" }}>
                      <span style={{ color: "#94A3B8", fontWeight: 600 }}>Next invoice #: </span>
                      <strong>{formatVendorNumber(t.numbering) || "—"}</strong>
                      {!t.isArchived && (
                        <button type="button" onClick={() => setNumbering(t)}
                          style={{ border: "none", background: "none", color: ACCENT, fontWeight: 700, fontSize: "0.74rem", marginLeft: 6, cursor: "pointer", padding: 0 }}>
                          Change
                        </button>
                      )}
                    </div>

                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: "auto", paddingTop: 4 }}>
                      {!t.isArchived ? (
                        <>
                          <button type="button" style={btn("primary")} onClick={() => openDesigner(t._id)}>
                            <FeatherIcon icon="edit-2" size={13} /> Design
                          </button>
                          {(!t.versionCount || t.hasUnsubmittedChanges || approvals.some((a) => a.status === "changes_requested")) && (
                            <button type="button" style={btn("secondary")} onClick={() => setSubmitting(t)}>
                              <FeatherIcon icon="send" size={13} /> Submit for approval
                            </button>
                          )}
                          <Tooltip title="Duplicate" arrow>
                            <button type="button" aria-label="Duplicate" style={btn("secondary")} disabled={busy}
                              onClick={() => run(t, () => duplicateTemplate(t._id), "Template duplicated")}>
                              <FeatherIcon icon="copy" size={13} />
                            </button>
                          </Tooltip>
                          {!t.isDefault && (
                            <Tooltip title="Use by default" arrow>
                              <button type="button" aria-label="Set as default" style={btn("secondary")} disabled={busy}
                                onClick={() => run(t, () => makeTemplateDefault(t._id), "Default template set")}>
                                <FeatherIcon icon="star" size={13} />
                              </button>
                            </Tooltip>
                          )}
                          <Tooltip title="Archive" arrow>
                            <button type="button" aria-label="Archive" style={btn("danger")} disabled={busy} onClick={() => archive(t)}>
                              <FeatherIcon icon="archive" size={13} />
                            </button>
                          </Tooltip>
                        </>
                      ) : (
                        <button type="button" style={btn("secondary")} disabled={busy}
                          onClick={() => run(t, () => archiveTemplate(t._id, true), "Template restored")}>
                          <FeatherIcon icon="rotate-ccw" size={13} /> Restore
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Paper>

      <StarterGallery
        isOpen={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        onCreated={(t) => { setGalleryOpen(false); if (t?._id) openDesigner(t._id); else load(); }}
      />
      <NumberingDialog template={numbering} onClose={() => setNumbering(null)} onSaved={() => { setNumbering(null); load(); }} />
      <SubmitDialog template={submitting} onClose={() => setSubmitting(null)} onSubmitted={() => { setSubmitting(null); load(); }} />
    </>
  );
};

export { TemplatesTab };
